import {
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import {
  ConfirmContext,
  type ConfirmContextValue,
  type ConfirmOptions,
} from "@/shared/contexts/confirm-context";

interface InternalState {
  open: boolean;
  pending: boolean;
  options: ConfirmOptions | null;
}

export const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<InternalState>({
    open: false,
    pending: false,
    options: null,
  });

  // Holds the resolver for the in-flight confirm() promise so the dialog's
  // button handlers can settle it. A ref avoids stale-closure issues.
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  const settle = useCallback((confirmed: boolean) => {
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
  }, []);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        // Single-dialog policy: a new confirm supersedes any prior pending one,
        // which we resolve as cancelled so no promise is left dangling.
        settle(false);
        resolverRef.current = resolve;
        setState({ open: true, pending: false, options });
      }),
    [settle]
  );

  const close = useCallback(
    (confirmed: boolean) => {
      setState((s) => ({ ...s, open: false, pending: false }));
      settle(confirmed);
    },
    [settle]
  );

  const handleConfirm = useCallback(async () => {
    const opts = state.options;
    if (!opts) return;

    // Plain yes/no prompt — no async work to await.
    if (!opts.onConfirm) {
      close(true);
      return;
    }

    setState((s) => ({ ...s, pending: true }));
    try {
      await opts.onConfirm();
      // Success: close and resolve true (mirrors "close on success").
      close(true);
    } catch {
      // Failure: keep the dialog OPEN, clear pending so the user can retry.
      // The global MutationCache.onError already toasted the error.
      setState((s) => ({ ...s, pending: false }));
    }
  }, [state.options, close]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      // Radix fires this on Esc / overlay click / Cancel button. Never close
      // out from under an in-flight async action.
      if (!next) {
        if (state.pending) return;
        close(false);
      }
    },
    [state.pending, close]
  );

  const value = useMemo<ConfirmContextValue>(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {state.options && (
        <ConfirmDialog
          open={state.open}
          onOpenChange={handleOpenChange}
          title={state.options.title}
          description={state.options.description}
          body={state.options.body}
          confirmLabel={state.options.confirmLabel}
          cancelLabel={state.options.cancelLabel}
          pendingLabel={state.options.pendingLabel}
          variant={state.options.variant}
          pending={state.pending}
          onConfirm={handleConfirm}
        />
      )}
    </ConfirmContext.Provider>
  );
};
